// 仕様書 SPECIFICATION.md セクション6.3, 7, 8 に対応する定数

export const MAX_WINDOWS = 3 as const;
export const PINCODE_LENGTH = 9 as const;
export const PINCODE_TTL_MS = 180_000 as const; // 3 分
export const PINCODE_MAX_ATTEMPTS = 3 as const;

export const SESSION_MAX_DURATION_MS = 3_600_000 as const; // 1 時間 (本番上限)

// WS heartbeat 間隔 (ms)。Cloudflare Workers の WebSocket idle timeout (約 100s)
// を回避するため、クライアント (host / viewer) はこの間隔で {type:"ping"} を送る。
export const SIGNALING_PING_INTERVAL_MS = 30_000 as const;

// シグナリング WS が一時切断した場合の自動再接続パラメータ。
// 既に P2P が確立済みなら映像/音声は WebRTC 側で継続するため、WS だけ
// 復旧させればセッションを途切れず保てる。指数バックオフ + jitter で
// サーバ側の同時再接続スパイクを避ける。
// 5 回 = backoff 1+2+4+8+16 ≈ 31s (±20% jitter で実測 25〜37s)。
// signaling の VIEWER_RECONNECT_GRACE_MS より小さく保つこと
// (大きいと grace 切れで host が PC を破棄し再接続が無意味になる)。
export const SIGNALING_RECONNECT_INITIAL_DELAY_MS = 1_000 as const;
export const SIGNALING_RECONNECT_MAX_DELAY_MS = 30_000 as const;
export const SIGNALING_RECONNECT_MAX_ATTEMPTS = 5 as const;
export const SIGNALING_RECONNECT_JITTER_RATIO = 0.2 as const;

// viewer の WS が close したとき、host への viewer-left 送信を遅延させる時間 (ms)。
// この猶予中に viewer が ?reconnect=1 で attach し直せば PC は維持される
// (host 側で onViewerLeft = PC 破棄 を遅らせる効果)。
// SIGNALING_RECONNECT 系の最大試行合計時間 (上述 ~37s) + バッファ。
export const VIEWER_RECONNECT_GRACE_MS = 45_000 as const;

// host の WS が close したとき、視聴側 WS を即クローズせず猶予を与える時間 (ms)。
// この間に host が再接続すれば viewer WS / P2P を維持できる (= 一時的な回線断で
// セッション全体が破棄されない)。host も viewer と同じ再接続バックオフ
// (最大 ~37s) を使うため、それを上回る値にする。
export const HOST_RECONNECT_GRACE_MS = 45_000 as const;

// サーバ (room.ts) がセッションを意図的に終了する際の WebSocket close(1000, reason)
// で使う理由文字列。クライアントはこれらの理由による 1000 クローズのみ「再接続不可
// (= サーバが意図的にこのセッションを終わらせた)」と判断して停止する。
// 一方、ランタイム停止/デプロイによる reason 無しの 1000 や異常断 (1006) は
// 一時的な切断とみなして再接続する。サーバ・クライアントで共有し文字列のドリフトを防ぐ。
export const CLOSE_REASON = {
  SESSION_EXPIRED: "session-expired",
  REPLACED_HOST: "replaced-by-new-host",
  REPLACED_VIEWER: "replaced-by-new-viewer",
  HOST_STOPPED: "host-stopped",
  HOST_DISCONNECTED: "host-disconnected",
} as const;

// クライアントが「再接続せず終了」と判断すべき close reason の一覧。
export const TERMINAL_CLOSE_REASONS: readonly string[] =
  Object.values(CLOSE_REASON);

// セッション自動終了の何 ms 前から警告表示するか。
export const SESSION_WARNING_THRESHOLD_MS = 300_000 as const; // 5 分

export const RATE_LIMIT_REQUESTS_PER_MINUTE = 10 as const;
export const RATE_LIMIT_PINCODES_PER_HOUR_PER_IP = 5 as const;

export const VIDEO_MAX_WIDTH = 1920 as const;
export const VIDEO_MAX_HEIGHT = 1080 as const;
export const VIDEO_FRAMERATE = 30 as const;

export const VIEWER_SETTINGS_STORAGE_KEY = "WinCast-viewer-settings" as const;

// リロード後にセッションを自動復帰させるため、直近の pincode を sessionStorage に
// 保持するキー。VIEWER_SETTINGS_STORAGE_KEY と対称に shared で一元管理する。
export const VIEWER_PINCODE_STORAGE_KEY =
  "WinCast-viewer-session-pincode" as const;

// リロード直後に自動 connect する際の遅延 (ms)。旧 WS の close がサーバで処理される
// 前に新規 attach すると "Viewer already attached" 409 レースになるため、
// わずかに待ってから接続する。サーバ側は「最後勝ち」で旧ソケットを置換するので
// 実害はないが、旧 close イベント未処理のタイミングを避ける保険。
export const VIEWER_RELOAD_RECONNECT_DELAY_MS = 50 as const;

// Phase 2: TURN は使わず STUN のみ。Phase 5 で Cloudflare Calls TURN を追加。
export const STUN_SERVERS = [
  "stun:stun.l.google.com:19302",
  "stun:stun.cloudflare.com:3478",
] as const;
